/**
 * SS Layout Components — Card, Modal, SplitLayout, TabBar, PageHeader
 * ─────────────────────────────────────────────────────────────────────────────
 * Usage:
 *   import { Card, Modal, SplitLayout, TabBar, PageHeader } from './layout';
 */
import React, { useEffect } from 'react';
import { COLOR, FONT, RADIUS, shadow } from './tokens';

// ── Card ──────────────────────────────────────────────────────────────────────
/**
 * @param {string}  accentColor - optional left-border accent colour (e.g. COLOR.blue)
 * @param {number}  padding     - inner padding in px (default 20)
 */
export function Card({ children, style: extra, padding = 20, accentColor }) {
  return (
    <div style={{
      background:   '#fff',
      borderRadius: 16,
      border:       '1px solid #ECE3E7',
      borderLeft:   accentColor ? `4px solid ${accentColor}` : '1px solid #ECE3E7',
      padding,
      boxShadow:    '0 1px 2px rgba(13,27,46,.04), 0 10px 30px -16px rgba(13,27,46,.10)',
      ...extra,
    }}>
      {children}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
/**
 * @param {boolean}         open     - controls visibility
 * @param {string}          title    - header text
 * @param {() => void}      onClose  - called on ✕ click or backdrop click
 * @param {number}          width    - max-width in px (default 520)
 * @param {boolean}         closeOnBackdrop - default true
 */
export function Modal({ open, title, onClose, children, width = 520, closeOnBackdrop = true, subtitle, fullscreen = false }) {
  // Trap escape key
  useEffect(() => {
    if (!open) return;
    const handler = e => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  // `fullscreen` renders a LARGE CENTERED dialog, not edge-to-edge — the
  // sidebar/topbar stay visible behind the dimmed backdrop so users keep
  // their navigation context. Accepts number (px) or string widths.
  const maxW = fullscreen
    ? 'min(1240px, calc(100vw - 64px))'
    : (typeof width === 'number' ? `${width}px` : width);

  return (
    <div
      style={{
        position:   'fixed', inset: 0,
        background: 'rgba(13,27,46,0.5)',
        backdropFilter: 'blur(3px)',
        WebkitBackdropFilter: 'blur(3px)',
        zIndex:     300,
        display:    'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding:    20,
      }}
      onClick={closeOnBackdrop ? e => { if (e.target === e.currentTarget) onClose?.(); } : undefined}
    >
      <div style={{
        background:   '#fff',
        borderRadius: 18,
        width:        '100%',
        maxWidth:     maxW,
        maxHeight:    '92vh',
        height:       'auto',
        overflowY:    'auto',
        boxShadow:    shadow.lg,
        display:      'flex',
        flexDirection:'column',
      }}>
        {/* Header */}
        <div style={{
          display:        'flex',
          alignItems:     subtitle ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          padding:        '16px 22px',
          borderBottom:   `1px solid ${COLOR.grayBorder}`,
          background:     '#f6eff2',
          borderRadius:   '18px 18px 0 0',
          flexShrink:     0,
        }}>
          <div>
            <div style={{ fontWeight: FONT.weight.bold, fontSize: FONT.size.md, color: COLOR.text }}>
              {title}
            </div>
            {subtitle && (
              <div style={{ fontSize: FONT.size.sm, color: COLOR.textMuted, marginTop: 2 }}>{subtitle}</div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: COLOR.graySubtle, border: `1px solid ${COLOR.grayBorder}`,
              fontSize: 16, cursor: 'pointer', color: COLOR.gray, lineHeight: 1,
              padding: '3px 8px', marginLeft: 12, flexShrink: 0, borderRadius: RADIUS.sm,
              fontFamily: FONT.family,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 22px', flex: 1 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ── SplitLayout ───────────────────────────────────────────────────────────────
/**
 * Classic list-left / detail-right layout used throughout SS pages.
 *
 * @param {React.ReactNode} list         - left panel content
 * @param {React.ReactNode} detail       - right panel content (null = full-width)
 * @param {number}          listWidth    - px width of list panel when detail shown (default 380)
 * @param {number}          maxHeight    - max-height of detail panel (default calc(100vh-160px))
 */
export function SplitLayout({ list, detail, listWidth = 380, gap = 20, maxHeight = 'calc(100vh - 160px)' }) {
  return (
    <div style={{ display: 'flex', gap, alignItems: 'flex-start' }}>
      {/* List */}
      <div style={{
        width:    detail ? listWidth : '100%',
        flexShrink: 0,
        minWidth: 0,
        transition: 'width .2s',
      }}>
        {list}
      </div>

      {/* Detail */}
      {detail && (
        <div style={{
          flex:      1,
          minWidth:  0,
          overflowY: 'auto',
          maxHeight,
        }}>
          {detail}
        </div>
      )}
    </div>
  );
}

// ── TabBar ────────────────────────────────────────────────────────────────────
/**
 * @param {{ id: string, label: string }[]} tabs
 * @param {string}                          active   - id of active tab
 * @param {(id: string) => void}            onChange
 */
export function TabBar({ tabs, active, onChange, style: extra }) {
  return (
    <div style={{
      display:      'flex',
      borderBottom: `1px solid ${COLOR.grayBorder}`,
      overflowX:    'auto',
      ...extra,
    }}>
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          style={{
            padding:         '10px 16px',
            background:      'none',
            border:          'none',
            borderBottom:    active === tab.id ? `2px solid ${COLOR.blue}` : '2px solid transparent',
            color:           active === tab.id ? COLOR.blue : COLOR.textMuted,
            fontSize:        FONT.size.base,
            fontWeight:      FONT.weight.semi,
            fontFamily:      FONT.family,
            cursor:          'pointer',
            whiteSpace:      'nowrap',
            transition:      'color .12s, border-color .12s',
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ── PageHeader ────────────────────────────────────────────────────────────────
/**
 * Standard top-of-page title + subtitle + action slot.
 *
 * <PageHeader title="User Accounts" subtitle="Manage system users" action={<Button>+ New</Button>} />
 */
export function PageHeader({ title, subtitle, action, style: extra }) {
  return (
    <div style={{
      display:       'flex',
      justifyContent:'space-between',
      alignItems:    'flex-start',
      marginBottom:  20,
      flexWrap:      'wrap',
      gap:           12,
      ...extra,
    }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: FONT.weight.xbold, color: COLOR.text, margin: 0, letterSpacing: '-0.4px' }}>
          {title}
        </h1>
        {subtitle && (
          <p style={{ fontSize: FONT.size.base, color: COLOR.textMuted, margin: '4px 0 0' }}>
            {subtitle}
          </p>
        )}
      </div>
      {action && <div style={{ flexShrink: 0 }}>{action}</div>}
    </div>
  );
}

// ── Section ───────────────────────────────────────────────────────────────────
/** Titled section divider inside a card / detail panel. */
export function Section({ title, action, children, style: extra }) {
  return (
    <div style={{ marginBottom: 20, ...extra }}>
      {(title || action) && (
        <div style={{
          display:       'flex',
          justifyContent:'space-between',
          alignItems:    'center',
          marginBottom:  10,
        }}>
          {title && (
            <div style={{ fontSize: FONT.size.base, fontWeight: FONT.weight.bold, color: COLOR.text }}>
              {title}
            </div>
          )}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

// ── Page wrapper ──────────────────────────────────────────────────────────────
export function Page({ children, style: extra }) {
  return (
    <div style={{
      fontFamily: FONT.family,
      color:      COLOR.text,
      ...extra,
    }}>
      {children}
    </div>
  );
}
